/**
 * Per-product brand identity registry. Field names mirror
 * products/<product>.yaml where they overlap (productName ⇄ product,
 * displayName ⇄ display_name, webBaseUrl ⇄ web_base_url,
 * apiBaseUrl ⇄ api_base_url).
 *
 * Lives in @degaus/shared so every layer that emits brand identity can read
 * it: the API (error messages, upsell hints), backend-utils (outbound email
 * From headers), and features (notification senders, deep links).
 * backend-utils/features cannot import from apps/api-service, which keeps the
 * per-request product scope (AsyncLocalStorage) in
 * apps/api-service/src/brand/brand-config.ts — that module re-exports
 * everything here and layers the request-scoped resolution on top.
 *
 * accountUrl / cliAccountUrl are the two distinct account destinations the
 * current wonda strings already hardcode ("/account" for upgrades,
 * "/cli/account" for CLI topups); they are kept as explicit fields so a
 * future product can override the path independently of webBaseUrl.
 *
 * Email identity fields are the single source of truth for outbound mail
 * identity — changing a wonda value is a real behavior change on outbound
 * mail. Product emails (fromAddress) send from wonda.sh; the remaining
 * senders deliberately stay on wondercat.ai until each is migrated:
 *  - fromAddress: default From for transactional sends
 *    (packages/backend-utils/src/postmark/postmark.ts).
 *  - antiChurnFromAddress: default From for the anti-churn broadcast stream
 *    (overridable via ANTI_CHURN_FROM_EMAIL).
 *  - supportEmail: bare reply/contact address for user-facing copy.
 */
export type BrandConfig = {
  productName: string;
  displayName: string;
  webBaseUrl: string;
  apiBaseUrl: string;
  accountUrl: string;
  cliAccountUrl: string;
  fromAddress: string;
  antiChurnFromAddress: string;
  supportEmail: string;
};

/**
 * How a product authenticates its web sessions. This is auth behavior, not
 * brand identity, so it lives on ProductRegistration rather than BrandConfig.
 * Historical union: Clerk is removed and every real login runs the Tank +
 * code-exchange flow (docs/auth/cross-domain-auth-split.md), so the modes
 * survive as registry labels, not as live branches.
 *  - "legacy": the retired pre-Tank wonda flow (Clerk session on the
 *    product's own domain). No code path reads it anymore.
 *  - "gateway-exchange": the cross-domain code-exchange handoff via the auth
 *    gateway.
 *  - "tank": the product authenticates end to end on its own Tank identity
 *    host.
 */
export type AuthMode = "legacy" | "gateway-exchange" | "tank";

/**
 * One deployed environment of a product's web BFF session backend
 * (apps/web/server/session): the request web hosts it serves, the fixed
 * gateway + API origins for that env, and the EXACT redirect_uri registered
 * for the product's confidential client (oauth/config.ts). The callbackUri
 * must byte-match the registered one, so it is fixed per env — never derived
 * from the incoming request host.
 */
type WebAuthEnv = {
  webHosts: string[];
  gatewayOrigin: string;
  apiOrigin: string;
  callbackUri: string;
};

/**
 * A registered product: its brand config plus the API hostnames (Host header,
 * lowercased, no port) that resolve to it and the web origins it serves
 * (used for CORS + redirect allowlists) and its auth mode. Registering a new
 * product = adding one entry here (brand + apiHosts + webOrigins + authMode)
 * and, on the infra side, pointing that host at the shared LB.
 */
type ProductRegistration = {
  brand: BrandConfig;
  apiHosts: string[];
  webOrigins: string[];
  authMode: AuthMode;
  /**
   * The product's web BFF gateway-exchange flow registration: the confidential
   * OAuth client id the API registers for the product's web app, plus its
   * per-env session environments. Products without a web BFF session omit it
   * (their web hosts never resolve an auth flow).
   */
  webAuth?: {
    clientId: string;
    envs: WebAuthEnv[];
  };
  /**
   * The product's Tank (first-party auth service) registration: the request
   * hosts (Host header, lowercased, no port) that resolve to it, each with
   * ITS environment's web-app origin (Tank's pages send users "home" after
   * login/sign-out, and the staging identity host must land on the staging
   * web app, never prod), and whether signups are waitlist-gated
   * (tank_waitlist). Products without a tank entry have no Tank deployment —
   * Tank 404s their hosts.
   */
  tank?: {
    /**
     * Each identity host with ITS environment's origins: webOrigin is where
     * Tank's pages send users "home"; apiOrigin is that environment's product
     * API — the OAuth AS Tank forwards /authorize to AND the account.created
     * webhook target. Per-host so staging talks to the staging AS and each
     * product's hosts talk to that product's own API.
     */
    hosts: { host: string; webOrigin: string; apiOrigin: string }[];
    requiresWaitlist: boolean;
    /**
     * Whether the product's Tank login offers Google sign-in. Opt-in per
     * product: without it the login page renders no Google button and the
     * /login/google/* routes fail closed as not-configured, so a product can
     * never inherit another tenant's OAuth client through the deployment-wide
     * env fallback.
     */
    googleLogin: boolean;
  };
};

/** The only product today; also the fallback for unknown/missing hosts. */
export const DEFAULT_PRODUCT = "wonda";

/**
 * The legacy auth gateway host (the Clerk-era login domain). Its auth
 * surfaces are retired: the host now bounces every page to the canonical app
 * host (apps/web/server/middleware/domain-redirect.ts), where the same path
 * works. Kept registered (webOrigins + the /cli-auth URL the public API still
 * hands out) so old links keep completing until the DNS record is deleted.
 */
export const AUTH_GATEWAY_URL = "https://login.wonda.sh";

/**
 * Staging sibling of AUTH_GATEWAY_URL, same retired-gateway status on the
 * staging Cloud Run stack.
 */
export const AUTH_GATEWAY_URL_STAGING = "https://login.staging.wonda.sh";

const WONDA: ProductRegistration = {
  apiHosts: ["api.wondercat.ai", "staging.api.wondercat.ai"],
  webOrigins: [
    "https://wonda.sh",
    "https://www.wonda.sh",
    "https://staging.wonda.sh",
    AUTH_GATEWAY_URL,
    // Staging sibling of the auth gateway (login.staging.wonda.sh): registered
    // here so the API CORS + redirect allowlists accept it.
    AUTH_GATEWAY_URL_STAGING,
  ],
  authMode: "legacy",
  tank: {
    // id.* is the user-facing identity domain (login page, /security,
    // passkeys). It is also the WebAuthn rpId: passkeys bind PERMANENTLY to
    // the host they were registered on, so id.* must never be renamed or
    // dropped once real users hold credentials. (The original tank.* hosts
    // were retired 2026-07-23 with their cert + DNS, before any real passkey
    // registrations.)
    // "localhost" resolves wonda for local dev; resolution strips the port
    // (mirroring resolveProductFromHost), so any local port works.
    hosts: [
      {
        host: "id.wonda.sh",
        webOrigin: "https://wonda.sh",
        apiOrigin: "https://api.wondercat.ai",
      },
      {
        host: "id.staging.wonda.sh",
        webOrigin: "https://staging.wonda.sh",
        apiOrigin: "https://staging.api.wondercat.ai",
      },
      // Local dev has no fixed web-app origin (VITE_PORT gives worktrees
      // unique ports) and Tank's logout allowlist deliberately excludes
      // localhost origins, so dev "home" is Tank's own root: every home
      // link stays on the Tank origin, where local auth testing lives.
      // Local api-service default port (apps/api-service src/index.ts).
      { host: "localhost", webOrigin: "/", apiOrigin: "http://localhost:4000" },
    ],
    // Signups closed: wonda is not taking new users right now. Tank rejects
    // account creation for emails without an approved tank_waitlist row and
    // the login page offers the join-the-waitlist form instead. Landing CTA
    // copy in apps/web derives from this same flag (wondaSignupsGated), so
    // flipping it back reopens signups and restores the CTAs in one deploy.
    // Re-enable/promotion steps: docs/tank/runbook.md "Waitlist" section.
    requiresWaitlist: true,
    googleLogin: true,
  },
  webAuth: {
    // The confidential OAuth client id the API registers for the wonda web app.
    clientId: "wonda-web",
    envs: [
      {
        webHosts: ["staging.wonda.sh"],
        gatewayOrigin: AUTH_GATEWAY_URL_STAGING,
        apiOrigin: "https://staging.api.wondercat.ai",
        callbackUri: "https://staging.wonda.sh/auth/callback",
      },
      {
        // The apex (wonda.sh) 301s to www upstream (the web server's
        // domain-redirect middleware), but map it too so a direct hit still
        // resolves the prod env.
        webHosts: ["www.wonda.sh", "wonda.sh"],
        gatewayOrigin: AUTH_GATEWAY_URL,
        apiOrigin: "https://api.wondercat.ai",
        callbackUri: "https://www.wonda.sh/auth/callback",
      },
    ],
  },
  brand: {
    productName: "wonda",
    displayName: "Wonda",
    webBaseUrl: "https://wonda.sh",
    apiBaseUrl: "https://api.wondercat.ai",
    accountUrl: "https://wonda.sh/account",
    cliAccountUrl: "https://wonda.sh/cli/account",
    fromAddress: "Wonda <hello@wonda.sh>",
    antiChurnFromAddress: "Mathieu Marc <mathieu@wondercat.ai>",
    supportEmail: "hello@wondercat.ai",
  },
};

/**
 * Registry-only TEST TENANT. The original authtest product was the throwaway
 * acceptance harness for the cross-domain auth split; the harness, its OAuth
 * client secret, and its serving surface were retired 2026-07-23 (wonda's own
 * staging full-move now exercises the gateway-exchange flow for real). The
 * registration survives ONLY so cross-product isolation tests have a second
 * product (tank suites resolve its identity host in-process; api-service
 * oauth tests seed its client with test-local env). It deploys NOTHING: no
 * apiHosts, no webOrigins, no webAuth, no DNS, no certs, no secrets.
 */
const AUTHTEST: ProductRegistration = {
  apiHosts: [],
  // Deliberately NO webOrigins: this tenant must never widen the API CORS /
  // redirect allowlists.
  webOrigins: [],
  authMode: "gateway-exchange",
  tank: {
    // Second Tank product, so cross-product isolation is exercisable end to
    // end (a host resolving authtest can never touch wonda credentials). Not
    // waitlist-gated: it also covers Tank's ungated-signup branch.
    hosts: [
      {
        host: "id.authtest.staging.wonda.sh",
        webOrigin: "https://authtest.staging.wonda.sh",
        apiOrigin: "https://staging.api.wondercat.ai",
      },
    ],
    requiresWaitlist: false,
    // Test tenant: opted in so suites can exercise the Google branch with
    // the deployment-wide test pair.
    googleLogin: true,
  },
  brand: {
    productName: "authtest",
    displayName: "Auth Test",
    webBaseUrl: "https://authtest.staging.wonda.sh",
    apiBaseUrl: "https://staging.api.wondercat.ai",
    accountUrl: "https://authtest.staging.wonda.sh/auth/authed",
    cliAccountUrl: "https://authtest.staging.wonda.sh/auth/authed",
    fromAddress: "Auth Test <hello@wondercat.ai>",
    antiChurnFromAddress: "Auth Test <hello@wondercat.ai>",
    supportEmail: "hello@wondercat.ai",
  },
};

/**
 * Vibecat: the internal proxy/device dashboard (separate monorepo), Tank's
 * second real product (Clerk -> Tank cutover, 2026-07). Its web/API deploys
 * live in the vibecat repo (Firebase Hosting + Cloud Run in the shared GCP
 * project); degaus serves ONLY its identity hosts. apiHosts and webOrigins
 * stay empty on purpose: vibecat requests never resolve on the degaus API,
 * and its origins must not widen the degaus CORS/redirect allowlists — the
 * scoped tank host entries below are its entire degaus surface.
 */
const VIBECAT: ProductRegistration = {
  apiHosts: [],
  webOrigins: [],
  // Tank end to end: keeps vibecat off the Clerk gateway's serve gate and
  // every legacy path.
  authMode: "tank",
  tank: {
    // rpId permanence: passkeys bind PERMANENTLY to these hosts once real
    // users register credentials — never rename or drop them after cutover.
    hosts: [
      {
        host: "id.vibecat.ai",
        webOrigin: "https://app.vibecat.ai",
        apiOrigin: "https://api.vibecat.ai",
      },
      {
        host: "dev.id.vibecat.ai",
        webOrigin: "https://dev.app.vibecat.ai",
        apiOrigin: "https://dev.api.vibecat.ai",
      },
    ],
    // Internal tool: the team self-provisions via OTP login, no invite gate.
    requiresWaitlist: false,
    // OTP-only at cutover: no Google button, and the routes fail closed so
    // the deployment-wide (wonda) client can never leak onto these hosts.
    googleLogin: false,
  },
  brand: {
    productName: "vibecat",
    displayName: "Vibecat",
    webBaseUrl: "https://app.vibecat.ai",
    apiBaseUrl: "https://api.vibecat.ai",
    accountUrl: "https://app.vibecat.ai",
    cliAccountUrl: "https://app.vibecat.ai",
    // OTP email is vibecat's only login path at cutover: this sender's
    // Postmark signature (vibecat.ai domain) MUST be verified before the
    // dark-mode override opens the hosts, or nobody can sign in.
    fromAddress: "Vibecat <no-reply@vibecat.ai>",
    antiChurnFromAddress: "Vibecat <no-reply@vibecat.ai>",
    supportEmail: "mihai@degaus.com",
  },
};

/**
 * Ledger: the accounting engine being productized (separate repo,
 * degausai/ledger), Tank's third product — registered on the Vibecat model.
 * Its web/API deploys live in the ledger repo (own Cloud Run in the shared
 * GCP project); degaus serves ONLY its identity hosts. apiHosts and
 * webOrigins stay empty on purpose: ledger requests never resolve on the
 * degaus API, and its origins must not widen the degaus CORS/redirect
 * allowlists — the scoped tank host entries below are its entire degaus
 * surface.
 *
 * NAMING CAVEAT: the final public product name/domain is NOT decided —
 * *.ledger.degaus.com is interim wiring for the staging integration
 * (ledger ADR 0001, M4). The WebAuthn rpId binds PERMANENTLY to the id.*
 * host once real users register passkeys, so the PROD host below MUST be
 * revisited before launch; the dev host is safe to use now (staging-only,
 * disposable credentials).
 */
const LEDGER: ProductRegistration = {
  apiHosts: [],
  webOrigins: [],
  // Tank end to end: no legacy path applies.
  authMode: "tank",
  tank: {
    // Staging-first: dev is the host the M4 integration exercises; the prod
    // host is registered for parity with the terraform cert but is subject
    // to the naming caveat above — revisit before any real passkey exists.
    hosts: [
      {
        host: "dev.id.ledger.degaus.com",
        webOrigin: "https://app.ledger.degaus.com",
        apiOrigin: "https://app.ledger.degaus.com",
      },
      {
        host: "id.ledger.degaus.com",
        webOrigin: "https://app.ledger.degaus.com",
        apiOrigin: "https://app.ledger.degaus.com",
      },
    ],
    // No invite gate for the staging integration.
    requiresWaitlist: false,
    // No Google button, and the routes fail closed so the deployment-wide
    // (wonda) client can never leak onto these hosts.
    googleLogin: false,
  },
  brand: {
    productName: "ledger",
    displayName: "Ledger",
    webBaseUrl: "https://app.ledger.degaus.com",
    apiBaseUrl: "https://app.ledger.degaus.com",
    accountUrl: "https://app.ledger.degaus.com",
    cliAccountUrl: "https://app.ledger.degaus.com",
    // OTP email is ledger's only passwordless path: this sender's Postmark
    // signature (ledger.degaus.com or a verified parent domain) MUST exist
    // before staging e2e, or nobody can sign in. Revisit with the final
    // domain decision.
    fromAddress: "Ledger <no-reply@ledger.degaus.com>",
    antiChurnFromAddress: "Ledger <no-reply@ledger.degaus.com>",
    supportEmail: "andu@degaus.com",
  },
};

const PRODUCT_REGISTRY: Record<string, ProductRegistration> = {
  wonda: WONDA,
  authtest: AUTHTEST,
  vibecat: VIBECAT,
  ledger: LEDGER,
};

// host → product index, built once from the registry.
const HOST_TO_PRODUCT: Record<string, string> = Object.fromEntries(
  Object.entries(PRODUCT_REGISTRY).flatMap(([product, { apiHosts }]) =>
    apiHosts.map((host) => [host, product]),
  ),
);

/**
 * Maps a request Host header to a product key. Unknown or missing hosts default
 * to the wonda product, so every current request resolves to "wonda".
 */
export function resolveProductFromHost(host: string | undefined): string {
  if (!host) return DEFAULT_PRODUCT;
  const normalized = host.trim().toLowerCase().split(":")[0] ?? "";
  return HOST_TO_PRODUCT[normalized] ?? DEFAULT_PRODUCT;
}

/**
 * Resolves the brand config for a product. Omitted or unregistered products
 * fall back to wonda. Request-scoped resolution (the no-arg call reading the
 * product bound to the current request) lives in the api-service brand module,
 * which wraps this with its AsyncLocalStorage scope.
 */
export function getBrandConfig(product?: string): BrandConfig {
  return (PRODUCT_REGISTRY[product ?? DEFAULT_PRODUCT] ?? WONDA).brand;
}

/**
 * Resolves the auth mode label for a product. Omitted or unregistered
 * products fall back to wonda (mirrors getBrandConfig). Historical: no
 * runtime branches on this anymore (see the AuthMode comment above).
 */
export function getAuthMode(product?: string): AuthMode {
  return (PRODUCT_REGISTRY[product ?? DEFAULT_PRODUCT] ?? WONDA).authMode;
}

/**
 * Every registered API hostname across all products. Consumed by the web
 * cli-auth page to validate the ?api_url= exchange target — replaces the
 * hardcoded per-host allowlist there.
 */
export function getRegisteredApiHosts(): string[] {
  return Object.values(PRODUCT_REGISTRY).flatMap(({ apiHosts }) => apiHosts);
}

/**
 * Every registered web origin across all products. Consumed by the API CORS
 * allowlist and the web redirect-URL allowlist.
 */
export function getRegisteredWebOrigins(): string[] {
  return Object.values(PRODUCT_REGISTRY).flatMap(
    ({ webOrigins }) => webOrigins,
  );
}

/**
 * The web BFF auth-flow config resolved for one request host: which product it
 * is, the confidential client id to authenticate as, and the fixed per-env
 * gateway/API origins + registered redirect_uri. Consumed by the web session
 * backend (apps/web/server/session/flow.ts) in place of its former hardcoded
 * wonda-only literals.
 */
export type WebAuthFlow = {
  product: string;
  clientId: string;
  gatewayOrigin: string;
  apiOrigin: string;
  callbackUri: string;
};

// web host → auth flow index, built once from the registry (mirrors
// HOST_TO_PRODUCT above).
const HOST_TO_WEB_AUTH_FLOW: Record<string, WebAuthFlow> = Object.fromEntries(
  Object.entries(PRODUCT_REGISTRY).flatMap(([product, { webAuth }]) => {
    if (!webAuth) return [];
    return webAuth.envs.flatMap((env) =>
      env.webHosts.map((host): [string, WebAuthFlow] => [
        host,
        {
          product,
          clientId: webAuth.clientId,
          gatewayOrigin: env.gatewayOrigin,
          apiOrigin: env.apiOrigin,
          callbackUri: env.callbackUri,
        },
      ]),
    );
  }),
);

/**
 * Maps a request web host to its product's web BFF auth-flow config. Unlike
 * resolveProductFromHost there is NO fallback: only hosts explicitly listed in
 * a product's webAuth envs resolve — everything else (including the gateway
 * hosts themselves) returns null.
 */
export function resolveWebAuthFlowFromHost(
  host: string | undefined,
): WebAuthFlow | null {
  if (!host) return null;
  const normalized = host.trim().toLowerCase().split(":")[0] ?? "";
  return HOST_TO_WEB_AUTH_FLOW[normalized] ?? null;
}

/**
 * A Tank product resolved from a request host: the product key plus its Tank
 * behavior flags. Consumed by the Tank service (apps/tank) per request.
 */
export type TankProduct = {
  product: string;
  requiresWaitlist: boolean;
  /** Whether this product's Tank login offers Google sign-in (opt-in). */
  googleLogin: boolean;
  /** The web-app origin of the ENVIRONMENT this identity host serves. */
  webOrigin: string;
  /** The product API origin of this environment: OAuth AS + webhook target. */
  apiOrigin: string;
};

// tank host → product index, built once from the registry (mirrors
// HOST_TO_PRODUCT above).
const HOST_TO_TANK_PRODUCT: Record<string, TankProduct> = Object.fromEntries(
  Object.entries(PRODUCT_REGISTRY).flatMap(([product, { tank }]) => {
    if (!tank) return [];
    return tank.hosts.map((entry): [string, TankProduct] => [
      entry.host,
      {
        product,
        requiresWaitlist: tank.requiresWaitlist,
        googleLogin: tank.googleLogin,
        webOrigin: entry.webOrigin,
        apiOrigin: entry.apiOrigin,
      },
    ]);
  }),
);

/**
 * Maps a request Host header to the Tank product it serves. Unlike
 * resolveProductFromHost there is NO fallback: only hosts listed in a
 * product's tank entry resolve — Tank 404s everything else.
 */
export function resolveTankProductFromHost(
  host: string | undefined,
): TankProduct | null {
  if (!host) return null;
  const normalized = host.trim().toLowerCase().split(":")[0] ?? "";
  return HOST_TO_TANK_PRODUCT[normalized] ?? null;
}

/**
 * Whether a product's Tank signups are waitlist-gated, looked up by product
 * key rather than request host: for callers off the request path (e.g. the
 * provisioning backstop) that hold a product but no identity-host context.
 */
export function productRequiresWaitlist(product: string): boolean {
  return PRODUCT_REGISTRY[product]?.tank?.requiresWaitlist ?? false;
}

/**
 * Whether wonda's Tank signups are waitlist-gated. Drives landing CTA copy in
 * apps/web ("Join the waitlist" instead of "Sign up"): the copy and the Tank
 * gate flip together with requiresWaitlist, in one deploy.
 */
export function wondaSignupsGated(): boolean {
  return productRequiresWaitlist(DEFAULT_PRODUCT);
}

/**
 * The origins Tank's /logout may return a browser to, scoped to the product
 * that resolved the request: that product's own tank web origins (its homes,
 * across environments) plus its own gateway sign-out origins. Scoped so one
 * tenant's logout can never bounce a browser to another tenant's home.
 * Deliberately NOT getRegisteredWebOrigins: that list is the degaus API CORS
 * allowlist, and a product may be a Tank tenant without ever entering wonda's
 * CORS surface. The dev "/" placeholder is excluded (a path, not an origin).
 */
export function getTankReturnOrigins(product: string): string[] {
  const registration = (
    PRODUCT_REGISTRY as Record<string, ProductRegistration | undefined>
  )[product];
  if (!registration) return [];
  const tankOrigins = (registration.tank?.hosts ?? [])
    .map((entry) => entry.webOrigin)
    .filter((origin) => origin.startsWith("http"));
  const gatewayOrigins = (registration.webAuth?.envs ?? []).map(
    (env) => env.gatewayOrigin,
  );
  return [...new Set([...tankOrigins, ...gatewayOrigins])];
}

/**
 * The OAuth client ids the product WEB APPS authenticate with (wonda-web and
 * any sibling product). These are not "connected apps" a user can disconnect:
 * the browser session IS one of these grants, so revoking it signs you out of
 * the page you clicked from and leaves nothing to click.
 */
export function getProductWebAuthClientIds(): string[] {
  return [
    ...new Set(
      Object.values(PRODUCT_REGISTRY)
        .map(({ webAuth }) => webAuth?.clientId)
        .filter((clientId): clientId is string => Boolean(clientId)),
    ),
  ];
}

/**
 * Every registered auth-gateway host across all products' webAuth envs
 * (deduplicated). Consumed by the SPA's gateway-host check
 * (apps/web/src/common/google-ads-host.ts) so the retired gateway hosts never
 * fire ad conversions.
 */
export function getRegisteredGatewayHosts(): string[] {
  return [
    ...new Set(
      Object.values(PRODUCT_REGISTRY).flatMap(({ webAuth }) =>
        (webAuth?.envs ?? []).map((env) => new URL(env.gatewayOrigin).host),
      ),
    ),
  ];
}
